import React, { useState, useEffect } from 'react';
import { serviceAPI, getAvailableEndpoints } from '../../../../services';
import { getSelectedEndpointUrl } from '../../../../services/endpoints/endpointStore';
import './ServiceManager.css';

const ServiceManager = () => {
  // State for services data
  const [services, setServices] = useState({
    tradeEngines: [],
    quoteEngines: [],
    publicInterfaces: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [currentService, setCurrentService] = useState(null);
  
  // Separate state for status updates to avoid resetting form data
  const [serviceStatus, setServiceStatus] = useState({});
  
  // Fetch services from the API
  const fetchServices = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching endpoints and status...');
      const [
        endpointsResponse, 
        statusResponse,
        quoteEndpointsResponse,
        quoteStatusResponse,
        publicInterfaceResponse
      ] = await Promise.all([
        serviceAPI.getAll(),
        serviceAPI.getStatus(),
        serviceAPI.getAllQuotes(),
        serviceAPI.getQuoteStatus(),
        serviceAPI.getPublicInterfaceStatus()
      ]);
      
      console.log('Trade Endpoints Response:', endpointsResponse.data);
      console.log('Trade Status Response:', statusResponse.data);
      console.log('Quote Endpoints Response:', quoteEndpointsResponse.data);
      console.log('Quote Status Response:', quoteStatusResponse.data);
      console.log('Public Interface Response:', publicInterfaceResponse.data);
      
      // Get the trade endpoints array and status information
      const endpoints = endpointsResponse.data?.endpoints || [];
      const engineStatuses = statusResponse.data?.engines || [];
      
      // Create a map of endpoint to status for trade engines
      const statusMap = new Map(
        engineStatuses.map(engine => [engine.endpoint, engine.status])
      );
      
      // Transform the trade endpoints
      const tradeEngines = endpoints.map(endpoint => {
        const status = statusMap.get(endpoint.endpoint) || {
          connected: false,
          isPrimary: false,
          connectionState: 'Unknown',
          role: 'Unknown'
        };
        
        return {
          endpoint: endpoint.endpoint,
          name: endpoint.name,
          description: endpoint.description || 'No description available',
          status: status.connectionState,
          role: status.role,
          isConnected: status.connected,
          isPrimary: status.isPrimary,
          port: parseInt(endpoint.endpoint.split(':')[1]),
          type: 'trade'
        };
      });

      // Transform quote endpoints
      const quoteEndpoints = (quoteEndpointsResponse.data?.endpoints || []).map(endpointData => {
        const quoteStatus = quoteStatusResponse.data?.quotes?.[0]?.status || {
          connected: false,
          connectionState: 'Unknown'
        };

        const [host, port] = endpointData.endpoint.split(':');

        return {
          endpoint: endpointData.endpoint,
          name: endpointData.name || `Quote Engine ${endpointData.endpoint}`,
          description: endpointData.description || `Quote engine endpoint for market data`,
          status: quoteStatus.connectionState || 'Unknown',
          role: 'N/A',
          isConnected: quoteStatus.connected || false,
          isPrimary: false,
          port: parseInt(port),
          type: 'quote'
        };
      });

      // Get available endpoints for naming
      const availableEndpoints = getAvailableEndpoints();
      const endpointUrls = availableEndpoints.map(ep => ep.url);

      // Transform public interface endpoints
      // The /instance/primary-status endpoint returns {success, isPrimary} indicating if this instance is primary
      // We use the config service URL as the public interface URL
      const publicInterfaceData = publicInterfaceResponse.data;
      console.log('Public Interface Response:', publicInterfaceData);

      // Get config service URL from cache
      const configUrl = getSelectedEndpointUrl();
      const publicUrl = configUrl.startsWith('http') ? configUrl : `https://${configUrl}`;

      const publicInterfaces = [];
      if (publicUrl) {
        const [host, portStr] = publicUrl.replace(/https?:\/\//, '').split(':');
        const port = portStr ? parseInt(portStr) : (publicUrl.includes('https://') ? 443 : 80);
        
        publicInterfaces.push({
          endpoint: `${host}${port !== 80 && port !== 443 ? `:${port}` : ''}`,
          name: 'Public API Interface',
          description: 'Public REST API interface for external access',
          status: 'Active',
          role: publicInterfaceData?.isPrimary ? 'PRIMARY' : 'SECONDARY',
          isConnected: true,
          isPrimary: publicInterfaceData?.isPrimary || false,
          port: port,
          type: 'public',
          url: publicUrl
        });
      }
      
      console.log('Transformed data:', { tradeEngines, quoteEngines: quoteEndpoints, publicInterfaces });
      setServices({ tradeEngines, quoteEngines: quoteEndpoints, publicInterfaces });
      setError(null);
    } catch (err) {
      console.error('Error fetching services:', err);
      setError(err.message || 'Failed to fetch services');
    } finally {
      setLoading(false);
    }
  };
  
  // Initial fetch on mount
  useEffect(() => {
    console.log('ServiceManager component mounted');
    fetchServices();
  }, []);

  // Update status polling to include quote endpoints
  useEffect(() => {
    console.log('Setting up status polling interval');
    const statusInterval = setInterval(async () => {
      try {
        console.log('Fetching status update...');
        const [statusResponse, quoteStatusResponse, publicInterfaceResponse] = await Promise.all([
          serviceAPI.getStatus(),
          serviceAPI.getQuoteStatus(),
          serviceAPI.getPublicInterfaceStatus()
        ]);
        
        const engineStatuses = statusResponse.data?.engines || [];
        const quoteStatuses = quoteStatusResponse.data?.status || {};
        
        // Create a status map to avoid full state replacement
        const newStatusMap = {};
        
        // Update trade engines status
        engineStatuses.forEach(engine => {
          newStatusMap[`trade_${engine.endpoint}`] = {
            status: engine.status?.connectionState || 'Unknown',
            role: engine.status?.role || 'Unknown',
            isConnected: engine.status?.connected || false,
            isPrimary: engine.status?.isPrimary || false
          };
        });
        
        // Update quote engines status
        const quoteStatus = quoteStatusResponse.data?.quotes?.[0]?.status || {
          connected: false,
          connectionState: 'Unknown'
        };
        newStatusMap['quote_engines'] = {
          status: quoteStatus.connectionState || 'Unknown',
          isConnected: quoteStatus.connected || false
        };
        
        // Update public interfaces status
        const publicInterfaceData = publicInterfaceResponse.data;
        const configUrl = getSelectedEndpointUrl();
        const publicUrl = configUrl.startsWith('http') ? configUrl : `https://${configUrl}`;

        if (publicUrl) {
          newStatusMap[`public_${publicUrl}`] = {
            status: 'Active',
            role: publicInterfaceData?.isPrimary ? 'Primary' : 'Secondary',
            isPrimary: publicInterfaceData?.isPrimary || false,
            isConnected: true
          };
        }
        
        // Update only the status map, not the full services state
        setServiceStatus(newStatusMap);
        
      } catch (err) {
        console.error('Error polling status:', err);
      }
    }, 30000);

    return () => clearInterval(statusInterval);
  }, []);

  // Handle opening the add dialog
  const handleOpenAddDialog = () => {
    setShowAddDialog(true);
  };
  
  // Handle closing the add dialog
  const handleCloseAddDialog = () => {
    setShowAddDialog(false);
  };
  
  // Handle opening the edit dialog
  const handleOpenEditDialog = (service) => {
    setCurrentService(service);
    setShowEditDialog(true);
  };
  
  // Handle closing the edit dialog
  const handleCloseEditDialog = () => {
    setShowEditDialog(false);
    setCurrentService(null);
  };
  
  // Handle submitting the add service form
  const handleAddService = async (formData) => {
    console.log('Adding new service:', formData);
    
    try {
      if (formData.type === 'Trade') {
        console.log('Creating new trade endpoint');
        const endpoint = `${formData.endpoint}:${formData.port}`;
        await serviceAPI.create({ 
          name: endpoint,
          description: `Trade engine endpoint for ${formData.endpoint}`
        });
      } else if (formData.type === 'Quote') {
        console.log('Creating new quote endpoint');
        await serviceAPI.createQuote({ 
          ip: formData.endpoint,
          port: formData.port
        });
      }
      
      handleCloseAddDialog();
      fetchServices();
    } catch (err) {
      console.error('Error adding service:', err);
      setError(`Failed to add service: ${err.message}`);
    }
  };
  
  // Handle submitting the edit service form
  const handleEditService = async (formData) => {
    console.log('Editing service:', formData);
    
    try {
      if (currentService.type === 'trade') {
        console.log('Updating trade endpoint:', currentService.endpoint);
        const endpoint = `${formData.endpoint}:${formData.port}`;
        await serviceAPI.update(currentService.endpoint, { 
          name: endpoint,
          description: currentService.description 
        });
      } else if (currentService.type === 'quote') {
        // Quote endpoints can't be updated, only deleted and recreated
        console.log('Recreating quote endpoint');
        await serviceAPI.deleteQuote(currentService.endpoint);
        await serviceAPI.createQuote({ 
          ip: formData.endpoint,
          port: formData.port
        });
      }
      
      handleCloseEditDialog();
      fetchServices();
    } catch (err) {
      console.error('Error editing service:', err);
      setError(`Failed to edit service: ${err.message}`);
    }
  };
  
  // Dismisses an error notification
  const dismissError = () => {
    setError(null);
  };
  
  // Handle service toggle (start/stop) or primary/secondary for public interfaces
  const handleToggleService = async (service, type) => {
    console.log(`Toggling ${type} service:`, service);
    
    try {
      if (type === 'Public') {
        await serviceAPI.updatePublicInterfacePrimary(service.url, !service.isPrimary);
        console.log(`Updated public interface primary status to ${!service.isPrimary}`);
      } else {
        // For future implementation: start/stop services
        console.log(`Start/stop functionality not implemented for ${type} services`);
      }
      
      fetchServices();
    } catch (err) {
      console.error(`Error toggling ${type} service:`, err);
      setError(`Failed to toggle service: ${err.message}`);
    }
  };
  
  // Handle service restart
  const handleRestartService = async (service, type) => {
    console.log(`Restarting ${type} service:`, service);
    // Add implementation to call the API
    alert(`Restart functionality not implemented for ${type} services`);
  };
  
  // Handle service delete
  const handleDeleteService = async (service, type) => {
    console.log(`Deleting ${type} service:`, service);
    
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        if (type === 'Trade') {
          await serviceAPI.delete(service.endpoint);
        } else if (type === 'Quote') {
          await serviceAPI.deleteQuote(service.endpoint);
        }
        
        fetchServices();
      } catch (err) {
        console.error(`Error deleting ${type} service:`, err);
        setError(`Failed to delete service: ${err.message}`);
      }
    }
  };
  
  // Determine status and role classes for styling
  const getStatusClass = (status, isConnected) => {
    if (!isConnected) return 'disconnected';
    switch (status?.toLowerCase()) {
      case 'active':
        return 'running';
      case 'inactive':
        return 'stopped';
      default:
        return 'unknown';
    }
  };

  const getRoleClass = (role, isPrimary) => {
    if (isPrimary) return 'primary';
    return role?.toLowerCase() === 'secondary' ? 'secondary' : 'unknown';
  };
  
  // Helper function to get status for a service
  const getServiceStatus = (service, type) => {
    if (type === 'Trade') {
      return serviceStatus[`trade_${service.endpoint}`] || {
        status: service.status || 'Unknown',
        role: service.role || 'Unknown',
        isConnected: service.isConnected || false,
        isPrimary: service.isPrimary || false
      };
    } else if (type === 'Quote') {
      return serviceStatus['quote_engines'] || {
        status: service.status || 'Unknown',
        isConnected: service.isConnected || false
      };
    } else if (type === 'Public') {
      return serviceStatus[`public_${service.url}`] || {
        status: service.status || 'Unknown',
        role: service.role || 'Secondary',
        isPrimary: service.isPrimary || false,
        isConnected: service.isConnected || false
      };
    }
    return {
      status: service.status || 'Unknown',
      role: service.role || 'Unknown',
      isConnected: service.isConnected || false,
      isPrimary: service.isPrimary || false
    };
  };
  
  // Render a list of services
  const renderServiceTable = (services, type) => {
    const serviceData = 
      type === 'Trade' ? services.tradeEngines :
      type === 'Quote' ? services.quoteEngines :
      services.publicInterfaces;
    
    if (!serviceData || serviceData.length === 0) {
      return (
        <div className="sm-no-data">
          <i className="fas fa-server fa-2x" style={{ marginBottom: '1rem', opacity: 0.6 }}></i>
          <p>No {type} services found</p>
        </div>
      );
    }
    
    return (
      <div className="sm-table-wrapper">
        <table className="sm-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Endpoint</th>
              <th>Status</th>
              <th>Role</th>
              <th>Port</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {serviceData.map((service, index) => {
              const status = getServiceStatus(service, type);
              return (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td data-full-url={service.endpoint}>{service.endpoint}</td>
                  <td>
                    <div className={`sm-status sm-status-${getStatusClass(status.status, status.isConnected)}`}>
                      <span className="sm-status-dot"></span>
                      {status.status}
                    </div>
                  </td>
                  <td>
                    <span className={`sm-role sm-role-${getRoleClass(status.role, status.isPrimary)}`}>
                      {status.role === 'N/A' ? 'N/A' : status.role}
                    </span>
                  </td>
                  <td>{service.port}</td>
                <td>
                  <div className="sm-actions">
                    {type === 'Public' && (
                      <button
                        className="sm-action-btn sm-action-toggle"
                        onClick={() => handleToggleService(service, type)}
                        title={status.isPrimary ? 'Switch to Secondary' : 'Switch to Primary'}
                        disabled={status.isPrimary}
                      >
                        <i className={`fas ${status.isPrimary ? 'fa-arrow-down' : 'fa-arrow-up'}`}></i>
                        <span style={{ marginLeft: '0.25rem', fontSize: '0.8rem' }}>
                          {status.isPrimary ? 'Primary' : 'To Primary'}
                        </span>
                      </button>
                    )}
                    {type !== 'Public' && (
                      <>
                        <button
                          className="sm-action-btn sm-action-toggle"
                          onClick={() => handleToggleService(service, type)}
                          title={status.status === 'Running' ? 'Stop' : 'Start'}
                        >
                          <i className="fas fa-power-off"></i>
                        </button>
                        <button
                          className="sm-action-btn sm-action-restart"
                          onClick={() => handleRestartService(service, type)}
                          title="Restart"
                        >
                          <i className="fas fa-sync"></i>
                        </button>
                        <button
                          className="sm-action-btn"
                          onClick={() => handleOpenEditDialog(service)}
                          title="Edit"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="sm-action-btn sm-action-delete"
                          onClick={() => handleDeleteService(service, type)}
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
        {type === 'Public' && (
          <div className="sm-info-note">
            <i className="fas fa-info-circle"></i>
            <span>Use the "To Primary" button to promote a secondary interface to primary.</span>
          </div>
        )}
      </div>
    );
  };
  
  const AddServiceDialog = ({ show, onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
      endpoint: '',
      port: '',
      type: 'Trade'
    });
    
    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData({ ...formData, [name]: value });
    };
    
    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };
    
    if (!show) return null;
    
    return (
      <div className="sm-modal" onClick={onClose}>
        <div className="sm-add-modal-content" onClick={e => e.stopPropagation()}>
          <div className="sm-add-modal-header">
            <span><i className="fas fa-plus"></i> Add Service</span>
            <button className="sm-add-close" onClick={onClose}>×</button>
          </div>
          <form className="sm-add-form" onSubmit={handleSubmit}>
            <div className="sm-add-row">
              <select
                className="sm-add-select"
                name="type"
                value={formData.type}
                onChange={handleChange}
              >
                <option value="Trade">Trade</option>
                <option value="Quote">Quote</option>
              </select>
              <input
                className="sm-add-input"
                type="text"
                name="endpoint"
                value={formData.endpoint}
                onChange={handleChange}
                placeholder="IP Address"
                pattern="^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$"
                required
              />
              <input
                className="sm-add-input sm-add-port"
                type="number"
                name="port"
                value={formData.port}
                onChange={handleChange}
                placeholder="Port"
                min="1"
                max="65535"
                required
              />
            </div>
            <div className="sm-add-actions">
              <button type="button" className="sm-add-btn-cancel" onClick={onClose}>Cancel</button>
              <button type="submit" className="sm-add-btn-submit">Add</button>
            </div>
          </form>
        </div>
      </div>
    );
  };
  
  const EditServiceDialog = ({ show, service, onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
      endpoint: '',
      port: ''
    });
    
    useEffect(() => {
      if (service) {
        const [ip, port] = service.endpoint.split(':');
        setFormData({
          endpoint: ip || '',
          port: port || ''
        });
      }
    }, [service]);
    
    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData({ ...formData, [name]: value });
    };
    
    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };
    
    if (!show || !service) return null;
    
    return (
      <div className="sm-modal" onClick={onClose}>
        <div className="sm-add-modal-content" onClick={e => e.stopPropagation()}>
          <div className="sm-add-modal-header">
            <span><i className="fas fa-edit"></i> Edit Service</span>
            <button className="sm-add-close" onClick={onClose}>×</button>
          </div>
          <form className="sm-add-form" onSubmit={handleSubmit}>
            <div className="sm-add-row">
              <input
                className="sm-add-input"
                type="text"
                name="endpoint"
                value={formData.endpoint}
                onChange={handleChange}
                placeholder="IP Address"
                pattern="^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$"
                required
              />
              <input
                className="sm-add-input sm-add-port"
                type="number"
                name="port"
                value={formData.port}
                onChange={handleChange}
                placeholder="Port"
                min="1"
                max="65535"
                required
              />
            </div>
            <div className="sm-add-actions">
              <button type="button" className="sm-add-btn-cancel" onClick={onClose}>Cancel</button>
              <button type="submit" className="sm-add-btn-submit">Update</button>
            </div>
          </form>
        </div>
      </div>
    );
  };
  
  return (
    <div className="sm-container">
      <div className="content-header">
        <h2>Service Manager</h2>
        <p>Manage and monitor trade services, quote services, and public interfaces.</p>
      </div>
      
      <div className="sm-header">
        <div className="sm-header-left">
          <button 
            className="sm-btn sm-btn-refresh" 
            onClick={fetchServices}
            disabled={loading}
          >
            <i className={`fas fa-sync ${loading ? 'fa-spin' : ''}`}></i>
            {!loading && <span>Load Services</span>}
          </button>
          <button className="sm-btn sm-btn-add" onClick={handleOpenAddDialog}>
            <i className="fas fa-plus"></i>
            <span>Add Service</span>
          </button>
        </div>
      </div>
      
      {error && (
        <div className="sm-error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{error}</span>
          <button className="sm-error-close" onClick={dismissError}>×</button>
        </div>
      )}
      
      {loading ? (
        <div className="sm-loading">
          <div className="LoadingSpinner medium">
            <div className="sae-spinner"></div>
            <div className="sae-text">Loading services data...</div>
          </div>
        </div>
      ) : (
        <div className="sm-services-grid">
          <div className="sm-section">
            <h3 className="sm-section-title">
              <i className="fas fa-chevron-right" style={{ marginRight: '0.5rem', fontSize: '0.875rem' }}></i>
              Trade Services
            </h3>
            {renderServiceTable(services, 'Trade')}
          </div>
          
          <div className="sm-section">
            <h3 className="sm-section-title">
              <i className="fas fa-chevron-right" style={{ marginRight: '0.5rem', fontSize: '0.875rem' }}></i>
              Quote Services
            </h3>
            {renderServiceTable(services, 'Quote')}
          </div>
          
          <div className="sm-section">
            <h3 className="sm-section-title">
              <i className="fas fa-chevron-right" style={{ marginRight: '0.5rem', fontSize: '0.875rem' }}></i>
              Public Interfaces
            </h3>
            {renderServiceTable(services, 'Public')}
          </div>
        </div>
      )}
      
      <AddServiceDialog 
        show={showAddDialog} 
        onClose={handleCloseAddDialog} 
        onSubmit={handleAddService} 
      />
      
      <EditServiceDialog 
        show={showEditDialog} 
        service={currentService} 
        onClose={handleCloseEditDialog} 
        onSubmit={handleEditService} 
      />
    </div>
  );
};

export default ServiceManager; 